// src/pages/auth/ChangePasswordPage.tsx
import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {
  Eye,
  EyeOff,
  Lock,
  Shield,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
} from "lucide-react";
import {useChangePasswordMutation} from "../../store/api/authApi";

import {Button, Input, Card} from "../../components/ui";
import {PageHeader} from "../../components/common";
import {validatePassword} from "../../utils/validators";
import {useToast} from "../../hooks/common/useToast";
import {useAuth} from "../../hooks";

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const {user} = useAuth();
  const {success, error: showError} = useToast();

  const [changePassword, {isLoading}] = useChangePasswordMutation();

  // Form state
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    newPasswordConfirmation: "",
  });

  const [errors, setErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    newPasswordConfirmation?: string;
    general?: string;
  }>({});

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [passwordStrength, setPasswordStrength] = useState<{
    score: number;
    feedback: string[];
  }>({score: 0, feedback: []});

  // Handle input changes
  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({...prev, [field]: value}));

    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({...prev, [field]: undefined}));
    }

    // Check password strength for new password
    if (field === "newPassword") {
      checkPasswordStrength(value);
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords((prev) => ({...prev, [field]: !prev[field]}));
  };

  // Check password strength
  const checkPasswordStrength = (password: string) => {
    const feedback: string[] = [];
    let score = 0;

    if (password.length >= 8) {
      score += 1;
    } else {
      feedback.push("At least 8 characters");
    }

    if (/[A-Z]/.test(password)) {
      score += 1;
    } else {
      feedback.push("One uppercase letter");
    }

    if (/[a-z]/.test(password)) {
      score += 1;
    } else {
      feedback.push("One lowercase letter");
    }

    if (/\d/.test(password)) {
      score += 1;
    } else {
      feedback.push("One number");
    }

    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      score += 1;
      feedback.push("Extra strong with special characters");
    }

    setPasswordStrength({score, feedback});
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Current password validation
    if (!formData.currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }

    // New password validation
    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required";
    } else {
      const passwordValidation = validatePassword(formData.newPassword);
      if (!passwordValidation.isValid) {
        newErrors.newPassword =
          passwordValidation.errors[0] ?? "Invalid password";
      }
    }

    // Confirm password validation
    if (!formData.newPasswordConfirmation) {
      newErrors.newPasswordConfirmation = "Please confirm your new password";
    } else if (formData.newPassword !== formData.newPasswordConfirmation) {
      newErrors.newPasswordConfirmation = "Passwords do not match";
    }

    // Check if new password is different from current
    if (
      formData.currentPassword &&
      formData.newPassword &&
      formData.currentPassword === formData.newPassword
    ) {
      newErrors.newPassword =
        "New password must be different from current password";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const response = await changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
        newPasswordConfirmation: formData.newPasswordConfirmation,
      }).unwrap();

      if (response.success) {
        success("Success", "Password changed successfully!");

        // Reset form
        setFormData({
          currentPassword: "",
          newPassword: "",
          newPasswordConfirmation: "",
        });
        setPasswordStrength({score: 0, feedback: []});

        // Navigate back or to dashboard
        navigate(-1);
      }
    } catch (err: any) {
      console.error("Change password error:", err);

      const errorMessage = err?.data?.message || "Failed to change password.";

      // Handle specific error cases
      if (
        errorMessage.includes("current password") ||
        errorMessage.includes("incorrect")
      ) {
        setErrors({currentPassword: "Current password is incorrect"});
      } else if (
        errorMessage.includes("same") ||
        errorMessage.includes("different")
      ) {
        setErrors({
          newPassword: "New password must be different from current password",
        });
      } else {
        setErrors({general: errorMessage});
      }

      showError("Error", errorMessage);
    }
  };

  // Get password strength color and text
  const getPasswordStrengthInfo = () => {
    switch (passwordStrength.score) {
      case 0:
      case 1:
        return {
          color: "bg-danger-500",
          text: "Weak",
          textColor: "text-danger-600",
        };
      case 2:
        return {
          color: "bg-warning-500",
          text: "Fair",
          textColor: "text-warning-600",
        };
      case 3:
        return {color: "bg-blue-500", text: "Good", textColor: "text-blue-600"};
      case 4:
        return {
          color: "bg-success-500",
          text: "Strong",
          textColor: "text-success-600",
        };
      case 5:
        return {
          color: "bg-success-600",
          text: "Very Strong",
          textColor: "text-success-700",
        };
      default:
        return {color: "bg-gray-300", text: "", textColor: "text-gray-500"};
    }
  };

  const strengthInfo = getPasswordStrengthInfo();

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Change Password"
        breadcrumbs={[
          {label: "Dashboard", path: "/"},
          {label: "Change Password"},
        ]}
      />

      <div className="max-w-2xl">
        <Card className="p-6">
          {/* User Info */}
          <div className="mb-6 pb-6 border-b border-gray-200 dark:border-dark-700">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Update Your Password
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Signed in as:{" "}
                  <span className="font-medium">{user?.email}</span>
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* General Error Message */}
            {errors.general && (
              <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4 flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-danger-500 flex-shrink-0" />
                <p className="text-sm text-danger-700 dark:text-danger-400">
                  {errors.general}
                </p>
              </div>
            )}

            {/* Current Password */}
            <div>
              <Input
                label="Current Password"
                type={showPasswords.current ? "text" : "password"}
                value={formData.currentPassword}
                onChange={(e) =>
                  handleInputChange("currentPassword", e.target.value)
                }
                placeholder="Enter your current password"
                leftIcon={<Lock className="w-4 h-4" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("current")}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPasswords.current ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                }
                error={errors.currentPassword}
                disabled={isLoading}
                autoComplete="current-password"
              />
            </div>

            {/* New Password */}
            <div>
              <Input
                label="New Password"
                type={showPasswords.new ? "text" : "password"}
                value={formData.newPassword}
                onChange={(e) =>
                  handleInputChange("newPassword", e.target.value)
                }
                placeholder="Enter your new password"
                leftIcon={<Lock className="w-4 h-4" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("new")}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPasswords.new ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                }
                error={errors.newPassword}
                disabled={isLoading}
                autoComplete="new-password"
              />

              {/* Password Strength Indicator */}
              {formData.newPassword && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Password strength:
                    </span>
                    <span
                      className={`text-sm font-medium ${strengthInfo.textColor}`}
                    >
                      {strengthInfo.text}
                    </span>
                  </div>

                  <div className="w-full bg-gray-200 dark:bg-dark-600 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${strengthInfo.color}`}
                      style={{width: `${(passwordStrength.score / 5) * 100}%`}}
                    />
                  </div>

                  {passwordStrength.feedback.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Requirements:
                      </p>
                      <ul className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                        {passwordStrength.feedback.map((item, index) => (
                          <li key={index} className="flex items-center">
                            <span className="w-1 h-1 bg-gray-400 rounded-full mr-2" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Confirm New Password */}
            <div>
              <Input
                label="Confirm New Password"
                type={showPasswords.confirm ? "text" : "password"}
                value={formData.newPasswordConfirmation}
                onChange={(e) =>
                  handleInputChange("newPasswordConfirmation", e.target.value)
                }
                placeholder="Confirm your new password"
                leftIcon={<Lock className="w-4 h-4" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility("confirm")}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPasswords.confirm ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                }
                error={errors.newPasswordConfirmation}
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>

            {/* Password Requirements */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                Password Requirements:
              </h4>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <li className="flex items-center">
                  <CheckCircle className="w-3 h-3 mr-2 flex-shrink-0" />
                  At least 8 characters long
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-3 h-3 mr-2 flex-shrink-0" />
                  Contains uppercase and lowercase letters
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-3 h-3 mr-2 flex-shrink-0" />
                  Contains at least one number
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-3 h-3 mr-2 flex-shrink-0" />
                  Different from your current password
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-6">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(-1)}
                disabled={isLoading}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Cancel
              </Button>

              <Button
                type="submit"
                variant="primary"
                loading={isLoading}
                disabled={isLoading || passwordStrength.score < 2}
              >
                {isLoading ? "Changing Password..." : "Change Password"}
              </Button>
            </div>
          </form>
        </Card>

        {/* Security Tips */}
        <Card className="p-6 mt-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Security Tips
          </h3>
          <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-4 h-4 text-success-500 mt-0.5 flex-shrink-0" />
              <p>Use a unique password that you don't use for other accounts</p>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-4 h-4 text-success-500 mt-0.5 flex-shrink-0" />
              <p>
                Consider using a password manager to generate and store strong
                passwords
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-4 h-4 text-success-500 mt-0.5 flex-shrink-0" />
              <p>
                Change your password regularly and immediately if you suspect
                it's compromised
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-4 h-4 text-success-500 mt-0.5 flex-shrink-0" />
              <p>
                Never share your password with anyone or store it in plain text
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
